import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { PageHeading, Section, Heading, Text, PlainTable, Modal, PopupWindow } from '../components/ui';
import { getBases, deleteBase } from '../services/api';
import CookNowModal from '../components/CookNowModal';
import BasesEditModal from '../components/BasesEditModal';

const Bases = () => {
    const { user, userProfile } = useAuth();
    const navigate = useNavigate();
    const [kernels, setKernels] = useState([]);
    const [filteredKernels, setFilteredKernels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [kernelToDelete, setKernelToDelete] = useState(null);
    
    // Filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [difficultyFilter, setDifficultyFilter] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState('grid');
    const [controlsExpanded, setControlsExpanded] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    
    // Popup state
    const [openPopups, setOpenPopups] = useState([]);
    
    // Cook Now Modal state
    const [cookNowModalOpen, setCookNowModalOpen] = useState(false);
    const [selectedKernelId, setSelectedKernelId] = useState(null);
    
    // Edit Modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [kernelToEdit, setKernelToEdit] = useState(null);

    useEffect(() => {
        const fetchKernels = async () => {
            try {
                let token = null;
                if (user) {
                    token = await user.getIdToken();
                }
                
                const availableKernels = await getBases(token, true); // includeImages = true
                setKernels(availableKernels);
                setFilteredKernels(availableKernels);
            } catch (err) {
                console.error('Error fetching kernels:', err);
                setError('Failed to load kernels');
            } finally {
                setLoading(false);
            }
        };

        fetchKernels();
    }, [user]);

    const handleDeleteKernel = async () => {
        if (!kernelToDelete || !user) return;

        try {
            const token = await user.getIdToken();
            await deleteBase(kernelToDelete.id, token);
            setKernels(kernels.filter(k => k.id !== kernelToDelete.id));
            setDeleteModalOpen(false);
            setKernelToDelete(null);
        } catch (err) {
            console.error('Error deleting kernel:', err);
            setError('Failed to delete kernel');
        }
    };

    const openDeleteModal = (kernel) => {
        setKernelToDelete(kernel);
        setDeleteModalOpen(true);
    };

    const openEditModal = (kernel) => {
        setKernelToEdit(kernel);
        setEditModalOpen(true);
    };

    const handleEditSuccess = () => {
        // Refresh the kernels list to show updated data
        const fetchKernels = async () => {
            try {
                let token = null;
                if (user) {
                    token = await user.getIdToken();
                }
                
                const availableKernels = await getBases(token, true); // includeImages = true
                setKernels(availableKernels);
                setFilteredKernels(availableKernels);
            } catch (err) {
                console.error('Error fetching kernels:', err);
                setError('Failed to load kernels');
            }
        };
        fetchKernels();
    };

    // Filter kernels based on search term, category, and ownership
    useEffect(() => {
        let filtered = kernels;

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(kernel =>
                kernel.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (kernel.creator_name && kernel.creator_name.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        // Category filter (based on title keywords and category)
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(kernel => {
                const title = kernel.title.toLowerCase();
                const category = (kernel.category || '').toLowerCase();
                
                // First check if the actual category field matches (exact match)
                // Only match if category is not empty and exactly matches the filter
                const categoryMatches = category && category.length > 0 && 
                                       category.toLowerCase() === categoryFilter.toLowerCase();
                
                // Then check title keywords
                let titleMatches = false;
                switch (categoryFilter) {
                    case 'baking':
                        titleMatches = title.includes('bake') || title.includes('bread') || title.includes('cake') || title.includes('pastry');
                        break;
                    case 'cooking':
                        titleMatches = title.includes('cook') || title.includes('sauté') || title.includes('fry') || title.includes('grill');
                        break;
                    case 'sauces':
                        titleMatches = title.includes('sauce') || title.includes('dressing') || title.includes('marinade');
                        break;
                    case 'desserts':
                        titleMatches = title.includes('dessert') || title.includes('sweet') || title.includes('ice cream');
                        break;
                    case 'breakfast':
                        titleMatches = title.includes('breakfast') || title.includes('omelette') || title.includes('pancake') || title.includes('egg');
                        break;
                    case 'lunch':
                        titleMatches = title.includes('lunch') || title.includes('sandwich') || title.includes('salad') || title.includes('wrap');
                        break;
                    case 'dinner':
                        titleMatches = title.includes('dinner') || title.includes('steak') || title.includes('roast') || title.includes('pasta');
                        break;
                    case 'appetizer':
                        titleMatches = title.includes('appetizer') || title.includes('starter') || title.includes('dip') || title.includes('finger');
                        break;
                    case 'side':
                        titleMatches = title.includes('side') || title.includes('vegetable') || title.includes('grain') || title.includes('potato');
                        break;
                    case 'soup':
                        titleMatches = title.includes('soup') || title.includes('broth') || title.includes('stew') || title.includes('bisque');
                        break;
                    case 'salad':
                        titleMatches = title.includes('salad') || title.includes('greens') || title.includes('lettuce') || title.includes('vegetable');
                        break;
                    case 'condiment':
                        titleMatches = title.includes('condiment') || title.includes('pickle') || title.includes('relish') || title.includes('chutney');
                        break;
                    case 'bread':
                        titleMatches = title.includes('bread') || title.includes('dough') || title.includes('yeast') || title.includes('focaccia');
                        break;
                    case 'vegetarian':
                        titleMatches = title.includes('vegetarian') || title.includes('vegan') || title.includes('plant') || title.includes('tofu');
                        break;
                    case 'technique':
                        titleMatches = title.includes('technique') || title.includes('method') || title.includes('fold') || title.includes('render');
                        break;
                    default:
                        titleMatches = false;
                }
                
                // Return true if either category matches OR title matches
                return categoryMatches || titleMatches;
            });
        }



        // Difficulty filter
        if (difficultyFilter !== 'all') {
            filtered = filtered.filter(kernel => kernel.difficulty_level === difficultyFilter);
        }

        setFilteredKernels(filtered);
    }, [kernels, searchTerm, categoryFilter, difficultyFilter, user]);

    // Close all popups when ESC is pressed
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && openPopups.length > 0) {
                // Close the most recent popup
                setOpenPopups(prev => prev.slice(0, -1));
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [openPopups.length]);

    const clearFilters = () => {
        setSearchTerm('');
        setCategoryFilter('all');
        setDifficultyFilter('all');
    };

    // Handle kernel row click to open popup
    const handleKernelClick = (kernel, e) => {
        // Don't open popup if clicking on action buttons
        if (e.target.closest('button') || e.target.closest('a')) {
            return;
        }
        
        // On mobile screens (smaller than 640px), skip popup and go directly to cook now modal
        if (window.innerWidth < 640) {
            handleCookNow(kernel.id);
            return;
        }
        
        // Check if popup is already open for this kernel
        if (openPopups.some(popup => popup.kernel.id === kernel.id)) {
            return;
        }
        
        // Calculate diagonal position for fixed positioning (relative to viewport)
        const offset = openPopups.length * 32; // Increased offset for diagonal spacing
        
        // Start diagonal pattern from viewport coordinates
        const baseX = 96; // Start 96px from left edge of viewport
        const baseY = 96; // Start 96px from top edge of viewport
        
        // Create diagonal pattern: each popup moves down and right
        const left = baseX + offset;
        const top = baseY + offset;
        
        // Ensure popup doesn't go off-screen
        const popupWidth = 384; // w-96 = 384px
        const popupHeight = 400; // approximate height
        
        // If popup would go off the right edge, reset to left side but continue diagonal
        let finalLeft = left;
        let finalTop = top;
        
        if (left + popupWidth > window.innerWidth - 24) {
            // Reset to left side, but continue diagonal pattern
            finalLeft = 96;
            finalTop = top + 32; // Continue diagonal downward
        }
        
        // If popup would go off the bottom edge, reset to top but continue diagonal
        if (top + popupHeight > window.innerHeight - 24) {
            // Reset to top, but continue diagonal pattern
            finalTop = 96;
            finalLeft = left + 32; // Continue diagonal rightward
        }
        
        const newPopup = {
            kernel,
            id: Date.now(),
            style: {
                top: `${finalTop}px`,
                left: `${finalLeft}px`,
            }
        };
        
        setOpenPopups(prev => [...prev, newPopup]);
    };

    // Close specific popup
    const closePopup = (popupId) => {
        setOpenPopups(prev => prev.filter(popup => popup.id !== popupId));
    };

    // Handle Cook Now from popup
    const handleCookNow = (kernelId) => {
        setSelectedKernelId(kernelId);
        setCookNowModalOpen(true);
    };

    // Handle successful cook session
    const handleCookSuccess = (draftId) => {
        setCookNowModalOpen(false);
        setSelectedKernelId(null);
        navigate(`/live/${draftId}`);
    };

    // Base Card Component for Grid View
    const BaseCard = ({ kernel }) => {
        return (
            <div className="aspect-[4/5] bg-neutral-0 hairline transition-all duration-200 cursor-pointer group flex flex-col relative hover:shadow-popup"
                onClick={(e) => handleKernelClick(kernel, e)}
            >
                <div className="flex-1 overflow-hidden">
                    {kernel.images && kernel.images.length > 0 ? (
                        <img 
                            src={kernel.images[0].url || kernel.images[0]}
                            alt={`${kernel.title} preview`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        />
                    ) : (
                        <div className="w-full h-full bg-neutral-50 flex items-center justify-center">
                            <Text className="font-mono text-neutral-900 opacity-60">No Image</Text>
                        </div>
                    )}
                </div>
                
                <div className="p-3 flex-shrink-0">
                    <div className="flex items-start gap-2 mb-2">
                        {kernel.difficulty_level && (
                            <span className="w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 bg-neutral-50 text-neutral-900 hairline">
                                {kernel.difficulty_level === 'beginner' ? 'B' :
                                 kernel.difficulty_level === 'intermediate' ? 'I' :
                                 'A'}
                            </span>
                        )}
                        <Text className="font-medium text-neutral-900 mb-1 group-hover:opacity-60 transition-opacity text-sm leading-tight flex-1">
                            {kernel.title}
                        </Text>
                    </div>
                    
                    {kernel.context && (
                        <Text className="text-xs text-neutral-900 opacity-60 mb-2 line-clamp-2 leading-tight">
                            {kernel.context}
                        </Text>
                    )}
                    
                    <div className="flex items-center justify-between text-xs font-mono uppercase tracking-wider mb-2">
                        <span className="text-neutral-900 opacity-60">
                            {kernel.slots && typeof kernel.slots === 'object' ? 
                                `${Object.keys(kernel.slots).length} SLOTS` : '—'}
                        </span>
                        <span className="text-neutral-900 opacity-60">
                            {kernel.creator_name || 'Unknown'}
                        </span>
                    </div>
                    
                    <div className="flex gap-1 flex-wrap">
                        {kernel.category && (
                            <span className="px-1 md:px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider">
                                {kernel.category}
                            </span>
                        )}
                        {kernel.difficulty_level && (
                            <span className="px-1 md:px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider">
                                {kernel.difficulty_level}
                            </span>
                        )}
                        {kernel.time_range && (
                            <span className="px-1 md:px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider">
                                {kernel.time_range}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };



    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-8 lg:px-8 xl:px-12 py-16">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Text>Loading bases...</Text>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-8 lg:px-8 xl:px-12 py-16">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Heading level={2}>Error</Heading>
                        <Text>{error}</Text>
                    </div>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Heading level={2}>Authentication Required</Heading>
                        <Text>You need to be logged in to view bases.</Text>
                        <Link to="/login" className="nav-link mt-4 inline-block">LOGIN</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-neutral-50">
            <div className="w-full px-0 py-8">
                {/* Header */}
                <div className="page-heading-container hairline min-h-[32vh] md:min-h-[48vh] relative">
                    {/* Controls Button - Bottom Right of Heading */}
                    <div className="absolute bottom-3 right-3 md:bottom-4 md:right-4 z-10">
                        <button
                            type="button"
                            onClick={() => setControlsExpanded((prev) => !prev)}
                            aria-expanded={controlsExpanded}
                            aria-controls="bases-controls-panel"
                            aria-label={`${controlsExpanded ? 'Hide' : 'Show'} bases controls`}
                            className="flex items-center justify-center w-10 h-10 bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50 transition-colors"
                        >
                            <span className="text-xl leading-none">{controlsExpanded ? '▴' : '▾'}</span>
                        </button>
                    </div>
                    
                    <div className="page-heading-content absolute bottom-0 left-2 right-2 md:left-4 md:right-4">
                        <h1 className="font-sans text-hero text-neutral-900 tracking-tight font-medium">
                            BASES
                        </h1>
                        <p className="page-heading-subtitle font-mono uppercase tracking-wider text-neutral-900 opacity-60 mt-0 text-sm md:text-lg">
                            RECIPE TEMPLATES • {filteredKernels.length} BASE{filteredKernels.length !== 1 ? 'S' : ''}
                        </p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="bg-neutral-0 hairline minimal-padding vspace">
                    {controlsExpanded && (
                        <div id="bases-controls-panel" className="space-y-6 mb-6">
                            {/* Ultra-Compact Filters Row - Responsive Layout */}
                            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center sm:justify-end gap-2 sm:gap-1.5">
                                {/* Search Toggle */}
                                <button
                                    type="button"
                                    onClick={() => setShowSearch(!showSearch)}
                                    className={`px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-xs uppercase tracking-wider transition-colors ${
                                        searchTerm 
                                            ? 'bg-neutral-900 text-neutral-0' 
                                            : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0'
                                    }`}
                                >
                                    🔍
                                </button>

                                {/* Category Filter */}
                                <select
                                    value={categoryFilter}
                                    onChange={(e) => setCategoryFilter(e.target.value)}
                                    className="px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-xs uppercase tracking-wider w-20"
                                >
                                    <option value="all">CAT</option>
                                    <option value="baking">BAKING</option>
                                    <option value="cooking">COOKING</option>
                                    <option value="sauces">SAUCES</option>
                                    <option value="desserts">DESSERTS</option>
                                    <option value="breakfast">BREAKFAST</option>
                                    <option value="lunch">LUNCH</option>
                                    <option value="dinner">DINNER</option>
                                    <option value="appetizer">APPETIZER</option>
                                    <option value="side">SIDE</option>
                                    <option value="soup">SOUP</option>
                                    <option value="salad">SALAD</option>
                                    <option value="condiment">CONDIMENT</option>
                                    <option value="bread">BREAD</option>
                                    <option value="vegetarian">VEGETARIAN</option>
                                    <option value="technique">TECHNIQUE</option>
                                </select>

                                {/* Difficulty Filter */}
                                <select
                                    value={difficultyFilter}
                                    onChange={(e) => setDifficultyFilter(e.target.value)}
                                    className="px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-xs uppercase tracking-wider w-20"
                                >
                                    <option value="all">DIFF</option>
                                    <option value="beginner">BEGINNER</option>
                                    <option value="intermediate">INTERMEDIATE</option>
                                    <option value="advanced">ADVANCED</option>
                                </select>

                                {/* View Mode Toggle */}
                                <div className="flex items-center">
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('grid')}
                                        className={`px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-xs uppercase tracking-wider transition-colors ${
                                            viewMode === 'grid' 
                                                ? 'bg-neutral-900 text-neutral-0' 
                                                : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0'
                                        }`}
                                    >
                                        ⚏
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('list')}
                                        className={`px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-xs uppercase tracking-wider transition-colors ${
                                            viewMode === 'list' 
                                                ? 'bg-neutral-900 text-neutral-0' 
                                                : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0'
                                        }`}
                                    >
                                        ☰
                                    </button>
                                </div>

                                {/* Clear Filters */}
                                {(searchTerm || categoryFilter !== 'all' || difficultyFilter !== 'all') && (
                                    <button
                                        type="button"
                                        onClick={clearFilters}
                                        className="px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 bg-neutral-0 text-neutral-900 font-mono text-xs uppercase tracking-wider hover:bg-neutral-100 transition-colors"
                                    >
                                        ✕
                                    </button>
                                )}

                                {/* NEW BASE Button - Admin Only */}
                                {user && userProfile?.is_admin && (
                                    <Link 
                                        to="/bases/new" 
                                        className="px-3 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900 bg-neutral-50 text-neutral-900 font-mono text-xs uppercase tracking-wider hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
                                    >
                                        NEW BASE
                                    </Link>
                                )}

                                {/* Results Count */}
                                <span className="text-xs font-mono text-neutral-900 opacity-60 ml-1">
                                    {filteredKernels.length}/{kernels.length}
                                </span>
                            </div>

                            {/* Search Bar - Hidden by default */}
                            {showSearch && (
                                <div className="mt-2">
                                    <input
                                        type="text"
                                        placeholder="Search bases..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full px-2 py-1 border border-neutral-300 focus:outline-none focus:border-neutral-900 font-mono text-sm"
                                        autoFocus
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {!controlsExpanded && (searchTerm || categoryFilter !== 'all' || difficultyFilter !== 'all') && (
                        <div className="mb-3 text-xs md:text-sm font-mono text-neutral-900 opacity-60 flex items-center justify-between gap-2">
                            <span>Showing {filteredKernels.length} of {kernels.length} bases</span>
                            <button
                                type="button"
                                onClick={clearFilters}
                                className="px-2 py-1 bg-neutral-0 text-neutral-900 hairline uppercase tracking-wider hover:bg-neutral-100 transition-colors"
                            >
                                CLEAR
                            </button>
                        </div>
                    )}


                    {filteredKernels.length === 0 ? (
                        <div className="text-center py-12">
                            <Text>
                                {kernels.length === 0 
                                    ? 'No bases found. Create your first recipe template!' 
                                    : 'No bases match your current filters.'
                                }
                            </Text>
                            {kernels.length === 0 ? (
                                <Link to="/bases/new" className="nav-link">CREATE BASE</Link>
                            ) : (
                                <button onClick={clearFilters} className="nav-link">CLEAR FILTERS</button>
                            )}
                        </div>
                    ) : viewMode === 'grid' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredKernels.map((kernel) => (
                                <BaseCard key={kernel.id} kernel={kernel} />
                            ))}
                        </div>
                    ) : (
                        <div className="px-0">
                            {/* Column Headers - Hidden on smallest screens */}
                            <div className="hidden sm:grid grid-cols-4 gap-4 mb-4 px-0" style={{gridTemplateColumns: '2fr 1fr 1fr 1fr'}}>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    TITLE
                                </div>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    TEMPLATE SLOTS
                                </div>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    CREATED
                                </div>
                                <div className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                    ACTIONS
                                </div>
                            </div>

                            {/* Table - Extended to edges */}
                            <div className="bg-neutral-0 sm:bg-transparent">
                                {filteredKernels.map((kernel, index) => (
                                    <div 
                                        key={kernel.id} 
                                        className={`grid grid-cols-4 gap-3 py-1.5 px-0 hover:bg-neutral-50 transition-colors duration-120 cursor-pointer sm:border-none border border-neutral-200 relative sm:pr-0 pr-16 ${index > 0 ? 'mt-1.5' : ''}`}
                                        style={{gridTemplateColumns: '2fr 1fr 1fr 1fr'}}
                                        onClick={(e) => handleKernelClick(kernel, e)}
                                    >
                                        {/* Base Image - Square on the right side */}
                                        {kernel.images && kernel.images.length > 0 && (
                                            <div className="sm:hidden absolute right-1 top-1 bottom-1 w-auto h-auto flex-shrink-0">
                                                <img 
                                                    src={kernel.images[0].url || kernel.images[0]}
                                                    alt={`${kernel.title} preview`}
                                                    className="w-full h-full object-cover hairline"
                                                    style={{ 
                                                        width: 'calc(100% - 8px)', 
                                                        height: 'calc(100% - 8px)',
                                                        aspectRatio: '1 / 1',
                                                        objectFit: 'cover',
                                                        objectPosition: 'center'
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <div className="flex items-start min-w-0 pl-2 sm:pr-0 pr-24">
                                            <div className="flex items-start gap-2 min-w-0 flex-wrap w-full">
                                                {kernel.difficulty_level && (
                                                    <span className="w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0 bg-neutral-50 text-neutral-900 hairline">
                                                        {kernel.difficulty_level === 'beginner' ? 'B' :
                                                         kernel.difficulty_level === 'intermediate' ? 'I' :
                                                         'A'}
                                                    </span>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <Text className="font-medium text-neutral-900 break-words">
                                                        {kernel.title}
                                                    </Text>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center font-mono text-sm text-neutral-900 min-w-0 pl-2">
                                            {kernel.slots && typeof kernel.slots === 'object' ? (
                                                <div className="flex gap-1 min-w-0">
                                                    {Object.keys(kernel.slots).slice(0, 2).map((key, index) => (
                                                        <span key={index} className="px-2 py-1 bg-neutral-50 hairline text-xs font-mono uppercase tracking-wider flex-shrink-0">
                                                            {key}
                                                        </span>
                                                    ))}
                                                    {Object.keys(kernel.slots).length > 2 && (
                                                        <span className="text-xs text-neutral-900 opacity-60 flex-shrink-0">
                                                            +{Object.keys(kernel.slots).length - 2}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-neutral-900 opacity-40 text-xs">—</span>
                                            )}
                                        </div>
                                        <div className="flex items-center pl-2">
                                            <span className="font-mono text-sm text-neutral-900 opacity-60 sm:block hidden">
                                                {new Date(kernel.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <div className="flex items-center pl-2">
                                            <div className="flex space-x-1">
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCookNow(kernel.id);
                                                    }}
                                                    className="text-xs font-mono uppercase tracking-wider bg-neutral-900 text-neutral-0 px-2 py-1 hover:opacity-60 transition-opacity"
                                                >
                                                    COOK
                                                </button>

                                                {/* Show edit/delete for kernels owned by current user OR for admin users */}
                                                {((user && kernel.user_id === user.uid) || userProfile?.is_admin) && (
                                                    <>
                                                        {userProfile?.is_admin ? (
                                                            <button 
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openEditModal(kernel);
                                                                }}
                                                                className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity"
                                                            >
                                                                EDIT
                                                            </button>
                                                        ) : (
                                                            <Link 
                                                                to={`/bases/${kernel.id}/edit`}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity"
                                                            >
                                                                EDIT
                                                            </Link>
                                                        )}
                                                        <button 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openDeleteModal(kernel);
                                                            }}
                                                            className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity"
                                                        >
                                                            DEL
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Popup Windows */}
                {openPopups.map((popup) => (
                    <PopupWindow
                        key={popup.id}
                        title={popup.kernel.title}
                        images={popup.kernel.images}
                        kernelId={popup.kernel.id}
                        kernelData={popup.kernel}
                        onClose={() => closePopup(popup.id)}
                        onCookNow={handleCookNow}
                        style={popup.style}
                    />
                ))}

                {/* Cook Now Modal */}
                <CookNowModal
                    kernelId={selectedKernelId}
                    isOpen={cookNowModalOpen}
                    onClose={() => {
                        setCookNowModalOpen(false);
                        setSelectedKernelId(null);
                    }}
                    onSuccess={handleCookSuccess}
                />

                {/* Delete Confirmation Modal */}
                <Modal
                    isOpen={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    title="Delete Kernel"
                >
                    <div className="vspace">
                        <Text>
                            Are you sure you want to delete "{kernelToDelete?.title}"? This action cannot be undone.
                        </Text>
                        
                        <div className="flex space-x-4 pt-4">
                            <button
                                onClick={handleDeleteKernel}
                                className="px-4 py-2 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                            >
                                DELETE
                            </button>
                            <button
                                onClick={() => setDeleteModalOpen(false)}
                                className="px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                            >
                                CANCEL
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* Base Edit Modal */}
                <BasesEditModal
                    isOpen={editModalOpen}
                    onClose={() => {
                        setEditModalOpen(false);
                        setKernelToEdit(null);
                    }}
                    kernelId={kernelToEdit?.id}
                    onSuccess={handleEditSuccess}
                />
            </div>
        </div>
    );
};

export default Bases; 
